import {
  Select as ChakraSelect,
  Portal,
  createListCollection,
  type SelectRootProps,
  type SelectTriggerProps,
  type SelectValueTextProps,
  type SelectIndicatorProps,
  type SelectContentProps,
} from "@chakra-ui/react";
import { ReactNode, useMemo } from "react";

export interface SelectItem {
  value: string;
  label: string;
}

export interface SelectProps {
  // Collection or items array
  collection?: ReturnType<typeof createListCollection>;
  items?: SelectItem[];
  disabled?: boolean;

  // Root props
  defaultValue?: string[];
  value?: string[];
  onValueChange?: SelectRootProps["onValueChange"];
  rootProps?: Omit<
    SelectRootProps,
    "collection" | "defaultValue" | "value" | "onValueChange"
  >;

  // Trigger props
  triggerProps?: SelectTriggerProps;

  // ValueText props
  placeholder?: string;
  valueTextProps?: SelectValueTextProps;

  // Indicator
  isIndicatorShown?: boolean;
  indicatorProps?: SelectIndicatorProps;

  // Item indicator
  showItemIndicator?: boolean;

  // Custom rendering
  renderItem?: (item: SelectItem) => ReactNode;

  // Content props
  contentProps?: SelectContentProps;

  portalRef?: React.RefObject<HTMLElement | null>;
  positioning?: SelectRootProps["positioning"];
}

const defaultTriggerStyles: SelectTriggerProps = {
  borderRadius: "10px",
  border: "1px solid var(--Main-Stroke-Blue, #E5EFFF)",
  bg: "var(--Text-White, #FFF)",
  boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.10)",
  gap: 1,
  px: 4,
  py: 2,
};

export const Select = ({
  collection,
  items,
  disabled = false,
  defaultValue,
  value,
  onValueChange,
  rootProps = {},
  triggerProps = {},
  placeholder,
  valueTextProps = {},
  isIndicatorShown = true,
  indicatorProps = {},
  showItemIndicator = true,
  renderItem,
  contentProps = {},
  portalRef,
  positioning,
}: SelectProps) => {
  // Built once per items identity: rebuilding it every render made Chakra
  // re-derive the whole collection on each parent re-render.
  const selectCollection = useMemo(
    () => collection ?? (items ? createListCollection({ items }) : undefined),
    [collection, items],
  );

  // A menu portalled into a dialog has to stay inside it. Left to its default
  // the flip middleware only measures the viewport, so a trigger near the
  // dialog's bottom edge opens a menu that hangs outside the card; bounding it
  // by the portal container makes it flip above the trigger instead.
  // sameWidth is Chakra's own default; passing `positioning` replaces it
  // wholesale, so it has to be restated here or the menu shrinks to its text.
  const resolvedPositioning = useMemo(
    () =>
      portalRef || positioning
        ? {
            sameWidth: true,
            ...(portalRef
              ? { boundary: () => portalRef.current ?? document.documentElement }
              : {}),
            ...positioning,
          }
        : undefined,
    [portalRef, positioning],
  );

  if (!selectCollection) {
    throw new Error(
      "Select component requires either 'collection' or 'items' prop",
    );
  }

  return (
    <ChakraSelect.Root
      // Without these, every option of every Select sits mounted in the DOM at
      // all times, so an unrelated keystroke re-renders thousands of hidden
      // items (worst on long lists like the city picker). The trigger's label
      // still resolves from the collection, not the mounted items.
      lazyMount
      unmountOnExit
      collection={selectCollection}
      defaultValue={defaultValue}
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      positioning={resolvedPositioning}
      {...rootProps}
    >
      <ChakraSelect.Trigger
        {...defaultTriggerStyles}
        {...triggerProps}
        opacity={disabled ? 0.6 : triggerProps.opacity}
        cursor={disabled ? "not-allowed" : triggerProps.cursor}
      >
        <ChakraSelect.ValueText placeholder={placeholder} {...valueTextProps} />
        {isIndicatorShown && <ChakraSelect.Indicator {...indicatorProps} />}
      </ChakraSelect.Trigger>
      <Portal container={portalRef}>
        <ChakraSelect.Positioner>
          <ChakraSelect.Content {...contentProps}>
            {selectCollection.items.map((item: SelectItem) => (
              <ChakraSelect.Item key={item.value} item={item}>
                {renderItem ? renderItem(item) : item.label}
                {showItemIndicator && <ChakraSelect.ItemIndicator />}
              </ChakraSelect.Item>
            ))}
          </ChakraSelect.Content>
        </ChakraSelect.Positioner>
      </Portal>
    </ChakraSelect.Root>
  );
};
